import { useEffect, useState } from "react";
import { Menu, Phone, X, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo } from "@/lib/navigation";

const NAV_KEYS = [
  ["services", "/services"],
  ["whyUs", "#why-us"],
  ["analytics", "#analytics"],
  ["testimonials", "#testimonials"],
  ["faq", "#faq"],
  ["resources", "/resources"],
  ["contact", "#contact"],
];

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = useGoTo();
  const scrollTo = (href) => {
    setOpen(false);
    goTo(href);
  };

  const LangToggle = ({ testId, className = "" }) => (
    <button
      data-testid={testId}
      onClick={toggle}
      aria-label={lang === "en" ? "切换到中文" : "Switch to English"}
      className={`flex items-center gap-1.5 h-10 px-3 border border-ink-900/12 text-sm font-semibold text-ink-800 hover:border-brand-500 hover:text-brand-600 transition-colors rounded-sm ${className}`}
    >
      <Languages className="w-4 h-4 text-brand-500" />
      {t.nav.switchTo}
    </button>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 glass-header transition-all duration-300 ${
        scrolled ? "border-b border-ink-900/8 shadow-[0_1px_0_rgba(11,15,26,0.04)]" : "border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <button
          data-testid="header-logo"
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-3 group"
          aria-label="Standard Accounting & Tax Advisory"
        >
          <img
            src="/logo.png"
            alt="Standard Accounting & Tax Advisory"
            className="h-11 sm:h-[52px] w-auto"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_KEYS.map(([key, href]) => (
            <button
              key={href}
              data-testid={`nav-link-${href.replace(/^[#/]/, "")}`}
              onClick={() => scrollTo(href)}
              className="text-sm font-medium text-ink-600 hover:text-brand-600 transition-colors"
            >
              {t.nav[key]}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="header-phone-link"
            href="tel:+61433022898"
            className="flex items-center gap-2 text-sm font-medium text-ink-800 hover:text-brand-600 transition-colors mr-1"
          >
            <Phone className="w-4 h-4 text-brand-500" />
            <span className="hidden xl:inline">+61 433 022 898</span>
          </a>
          <LangToggle testId="lang-toggle" />
          <Button
            data-testid="header-book-cta"
            onClick={() => scrollTo("#book")}
            className="bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-0.5 transition-all duration-300 rounded-sm px-5 h-10 font-semibold tracking-wide shadow-sm"
          >
            {t.nav.book}
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <LangToggle testId="lang-toggle-mobile" className="h-9 px-2.5 md:hidden" />
          <button
            data-testid="mobile-menu-toggle"
            className="text-ink-900"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-ink-900/8 bg-cream-50/95 backdrop-blur-xl">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_KEYS.map(([key, href]) => (
              <button
                key={href}
                data-testid={`mobile-nav-${href.replace(/^[#/]/, "")}`}
                onClick={() => scrollTo(href)}
                className="text-left text-base font-medium text-ink-800 py-2 border-b border-ink-900/8"
              >
                {t.nav[key]}
              </button>
            ))}
            <a
              href="tel:+61433022898"
              data-testid="mobile-phone-link"
              className="flex items-center gap-2 text-sm text-ink-800 pt-2"
            >
              <Phone className="w-4 h-4 text-brand-500" />
              +61 433 022 898
            </a>
            <Button
              data-testid="mobile-book-cta"
              onClick={() => scrollTo("#book")}
              className="bg-brand-500 text-white hover:bg-brand-600 rounded-sm h-11 mt-2 font-semibold"
            >
              {t.nav.book}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
